// client/src/pages/admin/Profile.tsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Avatar,
  Upload,
  Space,
  Divider,
  message,
  Row,
  Col,
  Switch,
  Select,
  Typography,
  Alert,
  Spin
} from 'antd';
import {
  UserOutlined,
  SaveOutlined,
  UploadOutlined,
  KeyOutlined,
  BellOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { userService } from '../../services/userService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const { Text } = Typography;
const { Option } = Select;

const AdminProfile: React.FC = () => {
  const { updateUser } = useAuthStore();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch current user profile
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['currentProfile'],
    queryFn: () => userService.getCurrentProfile(),
  });

  // Update form when profile data is loaded
  useEffect(() => {
    if (profileData) {
      profileForm.setFieldsValue({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: profileData.phone,
        language: profileData.preferences?.language || 'fr',
        timezone: profileData.preferences?.timezone || 'Europe/Paris',
      });

      setNotificationPrefs({
        email_notifications: profileData.preferences?.notifications?.email ?? true,
        sms_notifications: profileData.preferences?.notifications?.sms ?? false,
        push_notifications: profileData.preferences?.notifications?.push ?? true,
      });
    }
  }, [profileData, profileForm]);

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
  });

  // Profile update mutation
  const profileMutation = useMutation({
    mutationFn: (data: any) => userService.updateProfile(data),
    onSuccess: (response: { message?: string; user?: any }) => {
      message.success(response.message || 'Profil mis à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['currentProfile'] });
      // Update auth store with new user data
      if (response.user) {
        updateUser(response.user);
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour du profil');
    },
  });

  // Password change mutation
  const passwordMutation = useMutation({
    mutationFn: (data: any) => userService.changePassword(data),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message || 'Mot de passe modifié avec succès');
      passwordForm.resetFields();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la modification du mot de passe');
    },
  });

  // Preferences update mutation
  const preferencesMutation = useMutation({
    mutationFn: (preferences: any) => userService.updatePreferences(preferences),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message || 'Préférences mises à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['currentProfile'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour des préférences');
    },
  });

  const handleProfileUpdate = async (values: any) => {
    const { language, timezone, ...profileData } = values;

    // Update profile data
    await profileMutation.mutateAsync(profileData);

    // Update preferences if language or timezone changed
    if (language || timezone) {
      await preferencesMutation.mutateAsync({
        language,
        timezone,
      });
    }
  };

  const handlePasswordChange = async (values: any) => {
    await passwordMutation.mutateAsync(values);
  };

  const handleAvatarUpload = async (info: any) => {
    // For now, we'll use a placeholder URL
    // In a real implementation, you would upload the file to a CDN or storage service
    if (info.file) {
      const avatarUrl = `https://ui-avatars.com/api/?name=${profileData?.first_name}+${profileData?.last_name}&size=200`;
      try {
        await userService.uploadAvatar(avatarUrl);
        message.success('Photo de profil mise à jour');
        queryClient.invalidateQueries({ queryKey: ['currentProfile'] });
      } catch (error: any) {
        message.error(error.response?.data?.error || 'Erreur lors du téléchargement');
      }
    }
  };

  const handleNotificationChange = async (key: string, value: boolean) => {
    const newPrefs = {
      ...notificationPrefs,
      [key]: value,
    };
    setNotificationPrefs(newPrefs);

    // Update preferences in backend
    await preferencesMutation.mutateAsync({
      notifications: {
        email: newPrefs.email_notifications,
        sms: newPrefs.sms_notifications,
        push: newPrefs.push_notifications,
      },
    });
  };

  // Show loading spinner while fetching profile
  if (profileLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* Informations du profil */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <UserOutlined />
                Mon Profil
              </Space>
            }
          >
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
              initialValues={{
                first_name: profileData?.first_name || '',
                last_name: profileData?.last_name || '',
                phone: profileData?.phone || '',
                language: profileData?.preferences?.language || 'fr',
                timezone: profileData?.preferences?.timezone || 'Europe/Paris'
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="first_name"
                    label="Prénom"
                    rules={[{ required: true, message: 'Le prénom est requis' }]}
                  >
                    <Input placeholder="Votre prénom" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="last_name"
                    label="Nom"
                    rules={[{ required: true, message: 'Le nom est requis' }]}
                  >
                    <Input placeholder="Votre nom" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Email"
                  >
                    <Input value={profileData?.email} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="phone"
                    label="Téléphone"
                  >
                    <Input placeholder="+33123456789" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="language"
                    label="Langue"
                  >
                    <Select>
                      <Option value="fr">Français</Option>
                      <Option value="en">English</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="timezone"
                    label="Fuseau horaire"
                  >
                    <Select>
                      <Option value="Europe/Paris">Europe/Paris</Option>
                      <Option value="Europe/London">Europe/London</Option>
                      <Option value="America/New_York">America/New_York</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={profileMutation.isPending}
                  icon={<SaveOutlined />}
                >
                  Mettre à jour le profil
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Photo de profil et informations */}
        <Col xs={24} lg={8}>
          <Card title="Photo de profil">
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={120}
                icon={<UserOutlined />}
                src={profileData?.avatar}
                style={{ marginBottom: '16px' }}
              />
              <br />
              <Upload
                name="avatar"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleAvatarUpload}
              >
                <Button icon={<UploadOutlined />}>
                  Changer la photo
                </Button>
              </Upload>
              <Divider />
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>{profileData?.first_name} {profileData?.last_name}</Text>
                </div>
                <div>
                  <Text type="secondary">{profileData?.email}</Text>
                </div>
                <div>
                  <Text type="secondary">{profileData?.role === 'admin' ? 'Administrateur' : profileData?.role === 'super_admin' ? 'Super Administrateur' : 'Chauffeur'}</Text>
                </div>
                <div>
                  <Text type="secondary">
                    Membre depuis {new Date(profileData?.created_at || '').toLocaleDateString('fr-FR')}
                  </Text>
                </div>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Changement de mot de passe */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <KeyOutlined />
                Sécurité
              </Space>
            }
          >
            <Alert
              message="Sécurité du mot de passe"
              description="Votre mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial."
              type="info"
              style={{ marginBottom: '16px' }}
            />
            
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
            >
              <Form.Item
                name="current_password"
                label="Mot de passe actuel"
                rules={[{ required: true, message: 'Le mot de passe actuel est requis' }]}
              >
                <Input.Password placeholder="Votre mot de passe actuel" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="new_password"
                    label="Nouveau mot de passe"
                    rules={[
                      { required: true, message: 'Le nouveau mot de passe est requis' },
                      { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères' }
                    ]}
                  >
                    <Input.Password placeholder="Nouveau mot de passe" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="confirm_password"
                    label="Confirmer le mot de passe"
                    dependencies={['new_password']}
                    rules={[
                      { required: true, message: 'Veuillez confirmer le mot de passe' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('new_password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="Confirmer le mot de passe" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={passwordMutation.isPending}
                  icon={<SecurityScanOutlined />}
                >
                  Changer le mot de passe
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Préférences de notification */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <BellOutlined />
                Notifications
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Notifications par email</Text>
                <Switch
                  checked={notificationPrefs.email_notifications}
                  onChange={(checked) => handleNotificationChange('email_notifications', checked)}
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Notifications SMS</Text>
                <Switch
                  checked={notificationPrefs.sms_notifications}
                  onChange={(checked) => handleNotificationChange('sms_notifications', checked)}
                  loading={preferencesMutation.isPending}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>Notifications push</Text>
                <Switch
                  checked={notificationPrefs.push_notifications}
                  onChange={(checked) => handleNotificationChange('push_notifications', checked)}
                  loading={preferencesMutation.isPending}
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminProfile;